import { Component, OnDestroy, OnInit } from '@angular/core';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { Router } from '@angular/router';
import { MunicipalityService } from '../core/municipality.service';
import { conditionalValidator, getFieldValue, ProgressSpinnerService, setFieldValue } from '@sigma/sigma-utils';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import moment, { Moment } from 'moment';
import 'moment/locale/it';
import 'moment/locale/el';
import { PrimeNG } from 'primeng/config';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DatePicker } from 'primeng/datepicker';
import { HttpErrorResponse } from '@angular/common/module.d-CnjH8Dlt';
import { AppointmentService } from '../entities/appointment/appointment.service';
import { ToastrService } from 'ngx-toastr';
import { IAppointments, ISearchAppointment } from '../entities/appointment/search-appointment.model';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { HttpResponse } from '@angular/common/http';
import { PolicyService } from '../entities/policy/policy.service';
import { NgxSpinnerComponent } from 'ngx-spinner';
import { MunicipalityHeaderComponent } from '../shared/municipality-header/municipality-header.component';

@Component({
  selector: 'app-appointment-booking',
  standalone: true,
  templateUrl: './appointment-booking.component.html',
  imports: [
    NgIf,
    AsyncPipe,
    TranslatePipe,
    ReactiveFormsModule,
    DatePicker,
    FormsModule,
    NgxSpinnerComponent,
    NgClass,
    MunicipalityHeaderComponent,
  ],
})
export class AppointmentBookingComponent implements OnInit, OnDestroy {
  protected readonly moment = moment;
  public MAX_DATE = moment().startOf('d').add(5, 'days').toDate();

  public searchAppointments: ISearchAppointment | null = null;
  // Full, unfiltered set of slots fetched once for the whole [today, MAX_DATE] window; filtered per selected day into searchAppointments.appointments
  private allAppointments: IAppointments[] = [];
  // Days within [today, MAX_DATE] that have zero available slots; passed to the date-picker so they can't be clicked
  public disabledDates: Date[] = [];

  public loaderText: string = 'appointment.booking.spinner.title';

  public specialties: { id: string; name: string }[] = [];
  public doctors: { id: string; name: string }[] = [];
  public a = null;
  // UI state: show calendar first, then ask for name
  public step: 'calendar' | 'intro' = 'calendar';

  current$ = this.municipalityService.current$;
  public searchDate = moment().startOf('day');
  public introForm: FormGroup = new FormGroup<any>({
    firstName: new FormControl<string | null>(null, Validators.required),
    lastName: new FormControl<string | null>(null, Validators.required),
  });
  public form: FormGroup = new FormGroup<any>({
    lastName: new FormControl(null, Validators.required),
    appointmentDate: new FormControl(null, Validators.required),
    specialty: new FormControl(),
    doctor: new FormControl(),
    appointment: new FormControl(),
    firstName: new FormControl(
      null,
      conditionalValidator(() => getFieldValue('appointment', this.form) != null, Validators.required),
    ),
  });

  constructor(
    private municipalityService: MunicipalityService,
    private appointmentService: AppointmentService,
    private progressSpinnerService: ProgressSpinnerService,
    private toastr: ToastrService,
    private reCaptchaV3Service: ReCaptchaV3Service,
    private translateService: TranslateService,
    private router: Router,
    private policyService: PolicyService,
    private primeng: PrimeNG,
  ) {}

  ngOnInit(): void {
    // sync moment + date-picker locale with current language
    this.applyLocale();
    this.langSub = this.translateService.onLangChange.subscribe(() => this.applyLocale());
    if (!this.municipalityService.getCurrent()) {
      // No selected municipality -> go back to home
      Promise.resolve().then(() => this.router.navigate(['/'], { replaceUrl: true }));
    }
    // Ensure we start from the calendar step
    this.resetToInitial();
    // firstName/lastName are still empty at this point; login() sends them blank until the intro step patches them in
    this.login(() => this.fetchData());
  }

  private langSub?: Subscription;

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  private get lang(): string {
    return this.translateService.currentLang || this.translateService.defaultLang || 'it';
  }

  private applyLocale(): void {
    moment.locale(this.lang);
    const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
    // English header: "Su Mo Tu…"; others: "Lun Mar Mer…"
    const dayNamesMin = this.lang === 'en' ? moment.weekdaysMin() : moment.weekdaysShort().map(cap);
    this.primeng.setTranslation({
      firstDayOfWeek: moment.localeData().firstDayOfWeek(),
      dayNames: moment.weekdays().map(cap),
      dayNamesShort: moment.weekdaysShort().map(cap),
      dayNamesMin,
      monthNames: moment.months().map(cap),
      monthNamesShort: moment.monthsShort().map(cap),
    });
  }

  // en: "Thursday, October 15, 2026"; it/el: "Giovedì 15 Ottobre 2026"
  formatSearchDate(value: Moment | Date): string {
    const text = moment(value).format(this.lang === 'en' ? 'dddd, MMMM D, YYYY' : 'dddd D MMMM YYYY');
    return text.replace(/(^|\s)\S/g, c => c.toUpperCase());
  }

  // Month shown in the date-picker; arrows are only offered while bookable days exist in that direction
  public viewMonth = moment().startOf('month');

  onMonthChange(event: { month?: number; year?: number }): void {
    if (event.month && event.year) {
      this.viewMonth = moment({ year: event.year, month: event.month - 1 });
    }
  }

  get canGoPrev(): boolean {
    return this.viewMonth.isAfter(moment().startOf('month'));
  }

  get canGoNext(): boolean {
    return this.viewMonth.isBefore(moment(this.MAX_DATE).startOf('month'));
  }

  backToHome() {
    this.router.navigateByUrl('/services');
  }

  proceedToIntro(): void {
    if (getFieldValue('appointment', this.form) == null) {
      return;
    }
    this.step = 'intro';
  }

  backToCalendar(): void {
    this.step = 'calendar';
  }

  // Walk-in kiosk: no name step any more (hard for elderly users on the on-screen keyboard).
  // "Prosegui" books straight away with a fixed name per municipality, e.g. Sambuca_Pistoiese_Citizen,
  // which the backend also uses as the login -> one reusable user per municipality.
  // Previous flow: proceedToIntro() -> intro form (first/last name) -> confirmAndBook()
  confirmWalkIn(): void {
    const municipality = this.municipalityService.getCurrent();
    if (getFieldValue('appointment', this.form) == null || !municipality) {
      return;
    }
    const citizen = municipality.id.replace(/ /g, '_') + '_Citizen';
    this.form.patchValue({ firstName: citizen, lastName: citizen });
    this.login(() => this.book());
  }

  confirmAndBook(): void {
    this.introForm.markAllAsTouched();
    if (this.introForm.invalid) {
      return;
    }
    // copy values into the main form
    const { firstName, lastName } = this.introForm.value as { firstName: string; lastName: string };
    this.form.patchValue({ firstName, lastName });
    this.login(() => this.book());
  }

  // Runs the check-policy handshake using whatever firstName/lastName are currently on the main
  // form (blank on initial calendar load, real values once the intro step has patched them in),
  // then invokes onSuccess once a valid token is stored.
  private login(onSuccess: () => void): void {
    this.reCaptcha('policyCheck').then(token => {
      // take(1): without it every login() left a live subscription, so picking a municipality later re-ran onSuccess (e.g. book())
      this.current$.pipe(take(1)).subscribe((currentMunicipality: any) => {
        this.progressSpinnerService.showProgressSpinner();
        this.loaderText = 'appointment.booking.spinner.title2';
        this.policyService
          .checkPolicy(currentMunicipality.id, getFieldValue('firstName', this.form), getFieldValue('lastName', this.form), token)
          .subscribe({
            next: () => {
              this.progressSpinnerService.hideProgessSpinner();
              onSuccess();
            },
            error: () => {
              this.toastr.error(this.translateService.instant('errors.policyCheckFailed'));
              this.progressSpinnerService.hideProgessSpinner();
            },
          });
      });
    });
  }

  resetToInitial(): void {
    this.step = 'calendar';
    this.introForm.reset();
    // clear any previous search results / selections
    this.searchAppointments = null;
    this.allAppointments = [];
    this.disabledDates = [];
    this.specialties = [];
    this.doctors = [];
    this.searchDate = moment().startOf('day');
  }

  public fetchData(): void {
    /**
     *  Dont show any dates if the date from the calendar exceeds the max date
     */
    if (moment(getFieldValue('month', this.form)).startOf('month').isAfter(moment(this.MAX_DATE))) {
      if (this.searchAppointments != null) {
        this.searchAppointments.availableDates = [];
      }
      return;
    }
    this.reCaptcha().then(token => {
      this.progressSpinnerService.showProgressSpinner();
      this.loaderText = 'appointment.booking.spinner.title2';
      this.appointmentService
        .getAppointments(moment(getFieldValue('month', this.form)).format('YYYY-MM-DD'), moment(this.MAX_DATE).format('YYYY-MM-DD'), token)
        .subscribe({
          next: (res: HttpResponse<ISearchAppointment>) => {
            if (res.body != null) {
              this.searchAppointments = Object.assign({}, res.body);
              res.body.appointments.forEach((a, i) => {
                a.from = moment(a.from as string);
                a.until = moment(a.until as string);
                a.id = i;
              });

              // Keep the full fetched window in memory; the visible list is filtered per selected day
              this.allAppointments = res.body.appointments;
              this.updateVisibleAppointments();

              this.searchAppointments.availableDates = [];
              res.body.appointments.forEach(a => {
                const startOfDayMoment = moment(a.from as Moment)
                  .clone()
                  .startOf('D');
                if (this.searchAppointments?.availableDates.findIndex(ad => ad.dt.isSame(startOfDayMoment)) === -1) {
                  this.searchAppointments.availableDates.push({
                    waitingRoom: a.waitingRoom,
                    dt: startOfDayMoment,
                    specialty: a.specialty,
                  });
                }
              });
              this.computeDisabledDates();
              this.specialties = [];
              // res.body.specialties.map(s => {
              //   this.translateService.get(s).subscribe(tr => {
              //     this.specialties.push({id: s, name: tr});
              //   });
              // });
            }
            console.log(this.searchAppointments);
            this.progressSpinnerService.hideProgessSpinner();
          },
          error: (err: HttpErrorResponse) => {
            console.error(err);
            this.toastr.error(this.translateService.instant('errors.' + err.error.errorKey));
            this.progressSpinnerService.hideProgessSpinner();
          },
        });
    });
  }

  // Called when the user picks a different date in the calendar; the whole window is already
  // fetched, so this just re-filters it locally instead of hitting the backend again.
  public onDateChange(): void {
    this.updateVisibleAppointments();
  }

  private updateVisibleAppointments(): void {
    if (!this.searchAppointments) {
      return;
    }
    const selectedDay = moment(this.searchDate).startOf('day');
    this.searchAppointments.appointments = this.allAppointments.filter(a => {
      const from = a.from as Moment;
      return from.clone().startOf('day').isSame(selectedDay) && from.isAfter(moment());
    });
  }

  // Builds the list of days in [today, MAX_DATE] that have no available slots at all,
  // so the date-picker can disable them via [disabledDates].
  private computeDisabledDates(): void {
    const disabled: Date[] = [];
    const last = moment(this.MAX_DATE).startOf('day');
    for (let day = moment().startOf('day'); !day.isAfter(last); day = day.clone().add(1, 'day')) {
      const hasSlots = this.searchAppointments?.availableDates.some(ad => ad.dt.isSame(day));
      if (!hasSlots) {
        disabled.push(day.toDate());
      }
    }
    this.disabledDates = disabled;
  }

  public selectAppointment(appointment: IAppointments): void {
    setFieldValue('appointmentDate', appointment?.from, this.form);
    setFieldValue('appointment', appointment, this.form);
  }

  public hasAvailableDate(date: any): boolean {
    const inputDate: Date = new Date(date.year, date.month, date.day);
    const formattedInputDate: Moment = moment(inputDate).startOf('day');

    if (this.searchAppointments?.availableDates) {
      return this.searchAppointments.availableDates.some(a => a.dt.isSame(formattedInputDate));
    }

    return false; // Return false if this.searchAppointments?.availableDates is undefined
  }

  public book(): void {
    this.loaderText = 'appointment.booking.spinner.title';
    const municipality = this.municipalityService.getCurrent();
    this.reCaptcha('book').then(token => {
      this.progressSpinnerService.showProgressSpinner();
      this.appointmentService.book(this.processForm(), token, municipality?.label || municipality?.fallbackLabel || null).subscribe({
        next: () => {
          this.toastr.success(this.translateService.instant('appointment.booking.success'));
          this.progressSpinnerService.hideProgessSpinner();
          // Navigate to confirmation page with details
          const state = {
            firstName: getFieldValue('firstName', this.form),
            lastName: getFieldValue('lastName', this.form),
            municipality: municipality?.label || municipality?.fallbackLabel,
            appointment: getFieldValue('appointment', this.form),
          };
          sessionStorage.setItem('appointmentState', JSON.stringify(state));
          this.router.navigate(['/appointments/confirmation']);
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.toastr.error(this.translateService.instant('errors.' + err.error.errorKey));
          this.progressSpinnerService.hideProgessSpinner();
        },
      });
    });
  }

  private processForm(): any {
    const toBeSaved = {
      lastName: getFieldValue('lastName', this.form),
      firstName: getFieldValue('firstName', this.form),
      mobile: getFieldValue('mobile', this.form)?.e164Number,
      email: getFieldValue('email', this.form),
      appointment: Object.assign({}, getFieldValue('appointment', this.form)),
    };
    toBeSaved.appointment.from = moment(toBeSaved.appointment.from).format('YYYY-MM-DDTHH:mm:ss');
    toBeSaved.appointment.until = moment(toBeSaved.appointment.until).format('YYYY-MM-DDTHH:mm:ss');
    return toBeSaved;
  }

  public reCaptcha(action?: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.reCaptchaV3Service.execute(action ?? 'appointmentSearch').subscribe({
        next(token) {
          resolve(token);
        },
        error(error) {
          console.error(error);
          reject(error);
        },
      });
    });
  }

  protected readonly getFieldValue = getFieldValue;
}
